// actionLogSnapshot — Serialiserar ring-buffer till kompakt tail-snapshot
// för attach till bug/feature-rapporter.
//
// Contract: (1) bara entries inom TTL-window, (2) cap på entry-count, (3)
// cap på total JSON-storlek. Alla tre clamps körs så snapshots är
// förutsägbart små även om bufferten är full.

import type { RedactedActionEntry } from '@/lib/actionLogRedact';
import { estimateBytes } from '@/lib/actionLogRedact';

export interface TimedEntry {
  entry: RedactedActionEntry;
  /** ms epoch när eventet loggades. */
  timestamp: number;
}

export interface SnapshotOptions {
  /** Max ålder (ms) för inkluderad entry. Default 180 000 = 180s. */
  windowMs?: number;
  /** Max antal entries i snapshot. Default 100. */
  maxEntries?: number;
  /** Max byte-budget för hela JSON. Default 16 KB. */
  maxBytes?: number;
  /** `Date.now()`-override för deterministiska tester. */
  now?: number;
}

export interface Snapshot {
  /** ms epoch när snapshot togs. */
  takenAt: number;
  /** Window-config som användes. */
  windowMs: number;
  /** Entries, sorterade nyast sist. */
  entries: RedactedActionEntry[];
  /** True om entries droppades pga cap:ar. */
  truncated: boolean;
  /** Hur många entries togs bort pga cap:ar. */
  droppedCount: number;
  /** Total byte-storlek av snapshot. */
  bytes: number;
}

export const DEFAULT_WINDOW_MS = 180_000; // 180s
export const DEFAULT_MAX_ENTRIES = 100;
export const DEFAULT_MAX_BYTES = 16_384; // 16 KB

export function buildSnapshot(
  timeline: TimedEntry[],
  opts: SnapshotOptions = {},
): Snapshot {
  const now = opts.now ?? Date.now();
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const maxEntries = opts.maxEntries ?? DEFAULT_MAX_ENTRIES;
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;

  // (1) window-filter
  const withinWindow = timeline.filter(t => now - t.timestamp <= windowMs);
  const totalBefore = withinWindow.length;

  // (2) count-cap: behåll NYASTE N
  const sorted = [...withinWindow].sort((a, b) => a.timestamp - b.timestamp);
  const capped = sorted.slice(Math.max(0, sorted.length - maxEntries));

  // (3) byte-cap: trimma från äldsta tills under budget
  const kept: RedactedActionEntry[] = [];
  let bytes = 2; // '[]'
  for (let i = capped.length - 1; i >= 0; i--) {
    const e = capped[i].entry;
    const entryBytes = estimateBytes(e) + 1; // komma
    if (bytes + entryBytes > maxBytes) break;
    kept.unshift(e);
    bytes += entryBytes;
  }

  const dropped = totalBefore - kept.length;

  return {
    takenAt: now,
    windowMs,
    entries: kept,
    truncated: dropped > 0,
    droppedCount: dropped,
    bytes,
  };
}

/** Stats för snapshot — används i UI/tester. */
export function summarizeSnapshot(snap: Snapshot): {
  totalEntries: number;
  byKind: Record<string, number>;
  timespanMs: number;
} {
  const byKind: Record<string, number> = {};
  for (const e of snap.entries) {
    byKind[e.kind] = (byKind[e.kind] ?? 0) + 1;
  }
  return {
    totalEntries: snap.entries.length,
    byKind,
    timespanMs: snap.windowMs,
  };
}

/** Filter ut bara entries av specifika kinds (för diagnostik). */
export function filterByKind(
  snap: Snapshot,
  kinds: string[],
): RedactedActionEntry[] {
  const set = new Set(kinds);
  return snap.entries.filter(e => set.has(e.kind));
}

/** Empty snapshot — används som default vid sign-out/clear. */
export function emptySnapshot(now: number = Date.now()): Snapshot {
  return {
    takenAt: now,
    windowMs: DEFAULT_WINDOW_MS,
    entries: [],
    truncated: false,
    droppedCount: 0,
    bytes: 2,
  };
}
